import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    Button,
    VStack,
    HStack,
    FormControl,
    FormLabel,
    FormErrorMessage,
    Input,
    Textarea,
    Select,
    Checkbox,
    Box,
    Text,
    Divider,
    Alert,
    AlertIcon,
    Spinner,
    Tag,
    TagLabel,
    TagCloseButton,
    useToast,
    Grid,
    GridItem,
    useColorModeValue,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
    IconButton
} from '@chakra-ui/react';
import ReactSelect from 'react-select';
import { ChevronDownIcon, AddIcon } from '@chakra-ui/icons';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

import apiService from '../../services/apiService';
import { ToastManager } from '../Common/ToastManager';

/**
 * Event Schedule Modal Component for creating and editing events
 */
function EventScheduleModal({ 
    isOpen, 
    onClose, 
    onEventCreated, 
    applications = [], 
    initialData = null,
    userProfile 
}) {
    // Form state
    const [formData, setFormData] = useState({
        eventTitle: '',
        eventDescription: '',
        applicationId: '',
        eventType: '',
        startDatetime: new Date(),
        endDatetime: new Date(Date.now() + 2 * 60 * 60 * 1000),
        emailSubject: '',
        emailBody: '',
        toAddresses: [],
        ccAddresses: [],  // Added CC addresses
        selectedClients: [],
        assignedUsers: []
    });
    
    // UI state
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [errors, setErrors] = useState({});
    const [availableClients, setAvailableClients] = useState([]);
    const [availableUsers, setAvailableUsers] = useState([]);
    const [conflicts, setConflicts] = useState([]);
    const [showConflicts, setShowConflicts] = useState(false);
    const [supportPersons, setSupportPersons] = useState([]);
    
    // Address input states
    const [toEmailInput, setToEmailInput] = useState('');
    const [ccEmailInput, setCcEmailInput] = useState('');  
    
    const toast = useToast();
    const bgColor = useColorModeValue('white', 'gray.800');
    
    // Initialize form data
    useEffect(() => {
        if (isOpen) {
            if (initialData) {
                
                // Convert client data to react-select format
                let selectedClients = [];
                if (initialData.selectedClients) {
                    const parsedClients = parseJsonArray(initialData.selectedClients);
                    selectedClients = parsedClients.map(client => ({
                        value: client.value || client.clientId,
                        label: client.label || client.clientName || client.value || client.clientId
                    }));
                }
                
                // Convert user data to react-select format  
                let assignedUsers = [];
                if (initialData.assignedUsers) {
                    const parsedUsers = parseJsonArray(initialData.assignedUsers);
                    assignedUsers = parsedUsers.map(user => ({
                        value: user.value || user.email,
                        label: user.label || user.displayName || user.value || user.email
                    }));
                }
                
                setFormData({
                    eventTitle: initialData.eventTitle || '',
                    eventDescription: initialData.eventDescription || '',
                    applicationId: initialData.applicationId ? parseInt(initialData.applicationId) : '',
                    eventType: initialData.eventType || '',
                    startDatetime: initialData.startDatetimeUtc ? new Date(initialData.startDatetimeUtc) : new Date(),
                    endDatetime: initialData.endDatetimeUtc ? new Date(initialData.endDatetimeUtc) : new Date(Date.now() + 2 * 60 * 60 * 1000),
                    emailSubject: initialData.emailSubject || '',
                    emailBody: initialData.emailBody || '',
                    toAddresses: parseEmailAddresses(initialData.toAddresses) || [],
                    ccAddresses: parseEmailAddresses(initialData.ccAddresses) || [],  
                    selectedClients: selectedClients,
                    assignedUsers: assignedUsers
                });
            } else {
                resetForm();
            }
            
            loadAvailableUsers();
        }
    }, [isOpen, initialData]);
    
    // Load clients when application changes
    useEffect(() => {
        if (formData.applicationId && parseInt(formData.applicationId) > 0) {
            loadAvailableClients(parseInt(formData.applicationId));
        } else {
            setAvailableClients([]);
            setFormData(prev => ({ ...prev, selectedClients: [] }));
        }
    }, [formData.applicationId]);

    useEffect(() => {
        if (formData.selectedClients && formData.selectedClients.length > 0) {
            loadSupportPersons(formData.selectedClients);
        } else {
            setSupportPersons([]);
        }
    }, [formData.selectedClients]);
    
    // Check for conflicts when datetime or application changes
    // useEffect(() => {
    //     if (formData.applicationId && formData.startDatetime && formData.endDatetime && parseInt(formData.applicationId) > 0) {
    //         checkEventConflicts();
    //     }
    // }, [formData.applicationId, formData.startDatetime, formData.endDatetime]);
    
    // Auto-generate email subject when event details change
    useEffect(() => {
        if (formData.eventTitle && formData.eventType) {
            const app = applications.find(a => parseInt(a.id) === parseInt(formData.applicationId));
            const appName = app ? app.appName : 'Application';
            setFormData(prev => ({
                ...prev,
                emailSubject: `${formData.eventType} : ${formData.eventTitle} - ${appName}`
            }));
        }
    }, [formData.eventTitle, formData.eventType, formData.applicationId, applications]);
    
    const parseEmailAddresses = (emailString) => {
        if (!emailString) return [];
        try {
            if (emailString.startsWith('[')) {
                return JSON.parse(emailString);
            } else {
                return emailString.split(/[,;]/).map(email => email.trim()).filter(email => email);
            }
        } catch {
            return emailString.split(/[,;]/).map(email => email.trim()).filter(email => email);
        }
    };
    
    const parseJsonArray = (jsonString) => {
        if (!jsonString) return [];
        try {
            if (Array.isArray(jsonString)) return jsonString;
            const parsed = JSON.parse(jsonString);
            // Handle both formats: [{value: "123"}] and ["email@example.com"]
            if (Array.isArray(parsed)) {
                return parsed.map(item => {
                    if (typeof item === 'object' && item.value) {
                        return item; // Already in correct format
                    }
                    return { value: item, label: item }; // Convert simple value to object
                });
            }
            return [];
        } catch {
            return [];
        }
    };
    
    const resetForm = () => {
        const defaultStartDate = new Date();
        const defaultEndDate = new Date(Date.now() + 2 * 60 * 60 * 1000);
        
        setFormData({
            eventTitle: '',
            eventDescription: '',
            applicationId: '',
            eventType: '',
            startDatetime: defaultStartDate,
            endDatetime: defaultEndDate,
            emailSubject: '',
            emailBody: '',
            toAddresses: [],
            ccAddresses: [],  
            selectedClients: [],
            assignedUsers: []
        });
        setErrors({});
        setConflicts([]);
        setShowConflicts(false);
        setToEmailInput('');
        setCcEmailInput('');  
    };
    
    const loadAvailableClients = async (applicationId) => {
        try {
            setLoading(true);
            
            const response = await apiService.getEventApplicationClients(applicationId);
            
            if (response?.success && response?.data) {
                setAvailableClients(response.data || []);
            } else {
                console.warn('No clients data or failed response:', response);
                setAvailableClients([]);
            }
        } catch (error) {
            console.error('Error loading clients for application', applicationId, ':', error);
            ToastManager.showError('Failed to load available clients for selected application');
            setAvailableClients([]);
        } finally {
            setLoading(false);
        }
    };

    const loadSupportPersons = async (selectedClients) => {
        try {
            const clientIds = selectedClients.map(client => client.value);
            
            // Make parallel API calls for all selected clients
            const promises = clientIds.map(clientId => 
                apiService.fetchSupportPersons(clientId)
            );
            
            const responses = await Promise.all(promises);
            
            // Flatten all responses into a single array
            const allSupportPersons = responses.flatMap(response => 
                response?.success && response?.data ? response.data : []
            );
            
            setSupportPersons(allSupportPersons);
        } catch (error) {
            console.error('Error loading support persons:', error);
            setSupportPersons([]);
        }
    };
    
    const loadAvailableUsers = async () => {
        try {
            const response = await apiService.getEventAvailableUsers();
            
            if (response?.success && response?.data) {
                setAvailableUsers(response.data || []);
            }
        } catch (error) {
            console.error('Error loading users:', error);
        }
    };
    
    const checkEventConflicts = async () => {
        try {
            const response = await apiService.checkEventConflicts(
                formData.startDatetime.toISOString(),
                formData.endDatetime.toISOString(),
                parseInt(formData.applicationId),
                initialData?.id || null
            );
            
            if (response?.success && response?.data) {
                setConflicts(response.data || []);
                setShowConflicts(response.data && response.data.length > 0);
            }
        } catch (error) {
            console.error('Error checking conflicts:', error);
        }
    };
    
    const handleInputChange = (field, value) => {
        
        // Special handling for applicationId to ensure it's numeric
        if (field === 'applicationId') {
            const numericValue = value ? parseInt(value) : '';
            setFormData(prev => ({
                ...prev,
                [field]: numericValue
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [field]: value
            }));
        }
        
        // Clear field error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: null
            }));
        }
    };
    
    // Handle react-select changes for clients and users
    const handleSelectChange = (field, selectedOptions) => {
        
        const selections = selectedOptions || [];
        setFormData(prev => ({ 
            ...prev, 
            [field]: selections 
        }));
        
        // Clear field error
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: null
            }));
        }
    };
    
    const handleDateTimeChange = (field, date) => {
        setFormData(prev => {
            const newData = { ...prev, [field]: date };
            
            // Ensure end time is after start time
            if (field === 'startDatetime' && date >= prev.endDatetime) {
                newData.endDatetime = new Date(date.getTime() + 2 * 60 * 60 * 1000);
            } else if (field === 'endDatetime' && date <= prev.startDatetime) {
                newData.startDatetime = new Date(date.getTime() - 2 * 60 * 60 * 1000);
            }
            
            return newData;
        });
    };
    
    const addEmailAddress = (field, inputField, setInputField) => {
        const email = inputField.trim();
        if (email && validateEmail(email)) {
            // Check if email already exists in the list
            if (formData[field].includes(email)) {
                ToastManager.showWarning('This email address is already added');
                return;
            }
            
            setFormData(prev => ({
                ...prev,
                [field]: [...prev[field], email]
            }));
            setInputField('');
        } else if (email) {
            ToastManager.showError('Please enter a valid email address');
        }
    };
    
    const removeEmailAddress = (field, index) => {
        setFormData(prev => ({
            ...prev,
            [field]: prev[field].filter((_, i) => i !== index)
        }));
    };
    
    // Function to add assigned team emails to TO
    const addAssignedTeamEmails = () => {
        const assignedEmails = formData.assignedUsers.map(user => user.value).filter(email => email);
        
        setFormData(prev => {
            const existingEmails = prev.toAddresses || [];
            const newEmails = assignedEmails.filter(email => !existingEmails.includes(email));
            return {
                ...prev,
                toAddresses: [...existingEmails, ...newEmails]
            };
        });
        
        if (assignedEmails.length > 0) {
            ToastManager.showSuccess(`Added ${assignedEmails.length} team member(s) to TO`);
        }
    };

    // Function to add assigned team emails to CC
    const addAssignedTeamEmailsToCC = () => {
        const assignedEmails = formData.assignedUsers.map(user => user.value).filter(email => email);
        
        setFormData(prev => {
            const existingEmails = prev.ccAddresses || [];
            const newEmails = assignedEmails.filter(email => !existingEmails.includes(email));
            return {
                ...prev,
                ccAddresses: [...existingEmails, ...newEmails]
            };
        });
        
        if (assignedEmails.length > 0) {
            ToastManager.showSuccess(`Added ${assignedEmails.length} team member(s) to CC`);
        }
    };
    
    const validateEmail = (email) => {
        const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return regex.test(email);
    };
    
    const validateForm = () => {
        const newErrors = {};
        
        if (!formData.eventTitle.trim()) {
            newErrors.eventTitle = 'Event title is required';
        }
        
        if (!formData.applicationId || parseInt(formData.applicationId) <= 0) {
            newErrors.applicationId = 'Application is required';
        }
        
        if (!formData.eventType) {
            newErrors.eventType = 'Event type is required';
        }
        
        if (!formData.startDatetime) {
            newErrors.startDatetime = 'Start date and time is required';
        }
        
        if (!formData.endDatetime) {
            newErrors.endDatetime = 'End date and time is required';
        }
        
        if (formData.startDatetime && formData.endDatetime && formData.startDatetime >= formData.endDatetime) {
            newErrors.endDatetime = 'End date must be after start date';
        }
        
        if (formData.selectedClients.length === 0) {
            newErrors.selectedClients = 'At least one client must be selected';
        }
        
        if (formData.assignedUsers.length === 0) {
            newErrors.assignedUsers = 'At least one user must be assigned';
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };
    
    const handleSubmit = async () => {
        
        if (!validateForm()) {
            ToastManager.showError('Please fix all validation errors');
            return;
        }
        
        try {
            setSubmitting(true);
            
            const eventData = {
                eventTitle: formData.eventTitle.trim(),
                eventDescription: formData.eventDescription.trim(),
                applicationId: parseInt(formData.applicationId),
                eventType: formData.eventType,
                startDatetimeUtc: formData.startDatetime.toISOString(),
                endDatetimeUtc: formData.endDatetime.toISOString(),
                emailSubject: formData.emailSubject.trim(),
                emailBody: formData.emailBody.trim(),
                toAddresses: JSON.stringify(formData.toAddresses),
                ccAddresses: JSON.stringify(formData.ccAddresses),  // Added CC addresses
                bccAddresses: JSON.stringify([]),
                selectedClients: JSON.stringify(formData.selectedClients.map(client => ({ value: client.value }))),
                assignedUsers: JSON.stringify(formData.assignedUsers.map(user => ({ value: user.value })))
            };
            
            
            let response;
            if (initialData?.id) {
                response = await apiService.updateEventSchedule(initialData.id, eventData);
            } else {
                response = await apiService.createEventSchedule(eventData);
            }
            
            if (response?.success) {
                ToastManager.showSuccess(initialData?.id ? 'Event updated successfully' : 'Event scheduled successfully');
                onEventCreated();
            } else {
                ToastManager.showError(response?.message || 'Failed to save event schedule');
            }
            
        } catch (error) {
            console.error('Error saving event:', error);
            ToastManager.showError('Failed to save event schedule');
        } finally {
            setSubmitting(false);
        }
    };
    
    // Prepare options for react-select components
    const clientOptions = useMemo(() => {
        return availableClients.map(client => ({
            value: client.clientId.toString(),
            label: `${client.clientName}${client.version ? ` (${client.version})` : ''}`
        }));
    }, [availableClients]);
    

    // Update userOptions to combine both available users and support persons
    const userOptions = useMemo(() => {
        // Combine available users and support persons
        const combinedUsers = [
            ...availableUsers.map(user => ({
                value: user.email,
                label: `${user.displayName || user.email} (${user.role})`,
                source: 'general'
            })),
            ...supportPersons.map(person => ({
                value: person.email,
                label: `${person.name || person.email} (${person.team || 'Support'})`,
                source: 'support'
            }))
        ];
        
        // Remove duplicates based on email
        const uniqueUsers = combinedUsers.filter((user, index, self) =>
            index === self.findIndex(u => u.value === user.value)
        );
        
        return uniqueUsers;
    }, [availableUsers, supportPersons]);
    
    // Custom styles for react-select to match Chakra UI theme
    const selectStyles = {
        control: (provided, state) => ({
            ...provided,
            borderColor: state.isFocused ? '#3182ce' : '#e2e8f0',
            borderRadius: '6px',
            minHeight: '40px',
            boxShadow: state.isFocused ? '0 0 0 1px #3182ce' : 'none',
            '&:hover': {
                borderColor: state.isFocused ? '#3182ce' : '#cbd5e0'
            }
        }),
        multiValue: (provided) => ({
            ...provided,
            backgroundColor: '#edf2f7'
        }),
        multiValueLabel: (provided) => ({
            ...provided,
            color: '#2d3748'
        }),
        multiValueRemove: (provided) => ({
            ...provided,
            color: '#a0aec0',
            '&:hover': {
                backgroundColor: '#e53e3e',
                color: 'white'
            }
        })
    };
    
    return (
        <Modal 
            isOpen={isOpen} 
            onClose={onClose} 
            size="4xl" 
            scrollBehavior="inside"
            isCentered
        >
            <ModalOverlay />
            <ModalContent bg={bgColor} maxH="90vh">
                <ModalHeader>
                    {initialData?.id ? 'Edit Event Schedule' : 'Schedule New Event'}
                </ModalHeader>
                <ModalCloseButton />
                
                <ModalBody pb={6}>
                    <VStack spacing={6} align="stretch">
                        {/* Conflict Warning */}
                        {showConflicts && (
                            <Alert status="warning">
                                <AlertIcon />
                                <Box>
                                    <Text fontWeight="bold">Event Conflicts Detected</Text>
                                    <Text fontSize="sm">
                                        There are {conflicts.length} overlapping event(s) for this application and time period.
                                    </Text>
                                </Box>
                            </Alert>
                        )}
                        
                        {/* Basic Event Information */}
                        <Box>
                            <Text fontSize="lg" fontWeight="semibold" mb={4}>Event Details</Text>
                            <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                                <GridItem colSpan={2}>
                                    <FormControl isInvalid={!!errors.eventTitle}>
                                        <FormLabel>Event Title *</FormLabel>
                                        <Input
                                            value={formData.eventTitle}
                                            onChange={(e) => handleInputChange('eventTitle', e.target.value)}
                                            placeholder="Enter event title"
                                        />
                                        <FormErrorMessage>{errors.eventTitle}</FormErrorMessage>
                                    </FormControl>
                                </GridItem>
                                
                                <GridItem>
                                    <FormControl isInvalid={!!errors.applicationId}>
                                        <FormLabel>Application *</FormLabel>
                                        <Select
                                            value={formData.applicationId}
                                            onChange={(e) => handleInputChange('applicationId', e.target.value)}
                                            placeholder="Select application"
                                        >
                                            {applications.map(app => (
                                                <option key={app.id} value={app.id}>
                                                    {app.appName}
                                                </option>
                                            ))}
                                        </Select>
                                        <FormErrorMessage>{errors.applicationId}</FormErrorMessage>
                                    </FormControl>
                                </GridItem>
                                
                                <GridItem>
                                    <FormControl isInvalid={!!errors.eventType}>
                                        <FormLabel>Event Type *</FormLabel>
                                        <Select
                                            value={formData.eventType}
                                            onChange={(e) => handleInputChange('eventType', e.target.value)}
                                            placeholder="Select event type"
                                        >
                                            <option value="Deployment">Deployment</option>
                                            <option value="Upgrade">Upgrade</option>
                                            <option value="Maintenance">Maintenance</option>
                                        </Select>
                                        <FormErrorMessage>{errors.eventType}</FormErrorMessage>
                                    </FormControl>
                                </GridItem>
                                
                                <GridItem colSpan={2}>
                                    <FormControl>
                                        <FormLabel>Event Description</FormLabel>
                                        <Textarea
                                            value={formData.eventDescription}
                                            onChange={(e) => handleInputChange('eventDescription', e.target.value)}
                                            placeholder="Enter detailed description of the event"
                                            rows={3}
                                        />
                                    </FormControl>
                                </GridItem>
                            </Grid>
                        </Box>
                        
                        <Divider />
                        
                        {/* Date and Time Selection */}
                        <Box>
                            <Text fontSize="lg" fontWeight="semibold" mb={4}>Schedule</Text>
                            <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                                <GridItem>
                                    <FormControl isInvalid={!!errors.startDatetime}>
                                        <FormLabel>Start Date & Time *</FormLabel>
                                        <DatePicker
                                            selected={formData.startDatetime}
                                            onChange={(date) => handleDateTimeChange('startDatetime', date)}
                                            showTimeSelect
                                            timeIntervals={15}
                                            timeFormat="HH:mm"
                                            dateFormat="yyyy-MM-dd HH:mm"
                                            minDate={new Date()}
                                            customInput={<Input />}
                                        />
                                        <FormErrorMessage>{errors.startDatetime}</FormErrorMessage>
                                    </FormControl>
                                </GridItem>
                                
                                <GridItem>
                                    <FormControl isInvalid={!!errors.endDatetime}>
                                        <FormLabel>End Date & Time *</FormLabel>
                                        <DatePicker
                                            selected={formData.endDatetime}
                                            onChange={(date) => handleDateTimeChange('endDatetime', date)}
                                            showTimeSelect
                                            timeIntervals={15}
                                            timeFormat="HH:mm"
                                            dateFormat="yyyy-MM-dd HH:mm"
                                            minDate={formData.startDatetime}
                                            customInput={<Input />}
                                        />
                                        <FormErrorMessage>{errors.endDatetime}</FormErrorMessage>
                                    </FormControl>
                                </GridItem>
                            </Grid>
                        </Box>
                        
                        <Divider />
                        
                        {/* Client and User Selection */}
                        <Box>
                            <Text fontSize="lg" fontWeight="semibold" mb={4}>Targets</Text>
                            <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                                <GridItem>
                                    <FormControl isInvalid={!!errors.selectedClients}>
                                        <FormLabel>Affected Clients *</FormLabel>
                                        {loading ? (
                                            <Box textAlign="center" py={4}>
                                                <Spinner size="sm" />
                                                <Text fontSize="sm" mt={2}>Loading clients...</Text>
                                            </Box>
                                        ) : (
                                            <ReactSelect
                                                isMulti
                                                options={clientOptions}
                                                value={formData.selectedClients}
                                                onChange={(selectedOptions) => handleSelectChange('selectedClients', selectedOptions)}
                                                placeholder="Select affected clients"
                                                styles={selectStyles}
                                                closeMenuOnSelect={false}
                                                isSearchable={true}
                                                isClearable={false}
                                            />
                                        )}
                                        <FormErrorMessage>{errors.selectedClients}</FormErrorMessage>
                                    </FormControl>
                                </GridItem>
                                
                                <GridItem>
                                    <FormControl isInvalid={!!errors.assignedUsers}>
                                        <FormLabel>Assigned Team *</FormLabel>
                                        <ReactSelect
                                            isMulti
                                            options={userOptions}
                                            value={formData.assignedUsers}
                                            onChange={(selectedOptions) => handleSelectChange('assignedUsers', selectedOptions)}
                                            placeholder="Select assigned users"
                                            styles={selectStyles}
                                            closeMenuOnSelect={false}
                                            isSearchable={true}
                                            isClearable={false}
                                        />
                                        <FormErrorMessage>{errors.assignedUsers}</FormErrorMessage>
                                    </FormControl>
                                </GridItem>
                            </Grid>
                        </Box>
                        
                        <Divider />
                        
                        {/* Email Configuration */}
                        <Box>
                            <Text fontSize="lg" fontWeight="semibold" mb={4}>Email Notification</Text>
                            <VStack spacing={4} align="stretch">
                                {/* TO Addresses */}
                                <FormControl isInvalid={!!errors.toAddresses}>
                                    <FormLabel>To (Recipients)</FormLabel>
                                    <HStack>
                                        <Input
                                            value={toEmailInput}
                                            onChange={(e) => setToEmailInput(e.target.value)}
                                            placeholder="Enter email address"
                                            onKeyPress={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    addEmailAddress('toAddresses', toEmailInput, setToEmailInput);
                                                }
                                            }}
                                        />
                                        <Button 
                                            onClick={() => addEmailAddress('toAddresses', toEmailInput, setToEmailInput)}
                                            size="sm"
                                        >
                                            Add
                                        </Button>
                                        <Menu>
                                            <MenuButton
                                                as={IconButton}
                                                icon={<AddIcon />}
                                                size="sm"
                                                variant="outline"
                                                title="Add from assigned team"
                                                disabled={formData.assignedUsers.length === 0}
                                            />
                                            <MenuList>
                                                <MenuItem onClick={addAssignedTeamEmails}>
                                                    Add All Team Members
                                                </MenuItem>
                                            </MenuList>
                                        </Menu>
                                    </HStack>
                                    <Box mt={2}>
                                        {formData.toAddresses.map((email, index) => (
                                            <Tag key={index} size="md" colorScheme="blue" m={1}>
                                                <TagLabel>{email}</TagLabel>
                                                <TagCloseButton onClick={() => removeEmailAddress('toAddresses', index)} />
                                            </Tag>
                                        ))}
                                    </Box>
                                    <FormErrorMessage>{errors.toAddresses}</FormErrorMessage>
                                </FormControl>

                                {/* CC Addresses - NEW SECTION */}
                                <FormControl isInvalid={!!errors.ccAddresses}>
                                    <FormLabel>CC (Carbon Copy)</FormLabel>
                                    <HStack>
                                        <Input
                                            value={ccEmailInput}
                                            onChange={(e) => setCcEmailInput(e.target.value)}
                                            placeholder="Enter email address for CC"
                                            onKeyPress={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    addEmailAddress('ccAddresses', ccEmailInput, setCcEmailInput);
                                                }
                                            }}
                                        />
                                        <Button 
                                            onClick={() => addEmailAddress('ccAddresses', ccEmailInput, setCcEmailInput)}
                                            size="sm"
                                        >
                                            Add
                                        </Button>
                                        <Menu>
                                            <MenuButton
                                                as={IconButton}
                                                icon={<AddIcon />}
                                                size="sm"
                                                variant="outline"
                                                title="Add from assigned team"
                                                disabled={formData.assignedUsers.length === 0}
                                            />
                                            <MenuList>
                                                <MenuItem onClick={addAssignedTeamEmailsToCC}>
                                                    Add All Team Members
                                                </MenuItem>
                                            </MenuList>
                                        </Menu>
                                    </HStack>
                                    <Box mt={2}>
                                        {formData.ccAddresses.map((email, index) => (
                                            <Tag key={index} size="md" colorScheme="green" m={1}>
                                                <TagLabel>{email}</TagLabel>
                                                <TagCloseButton onClick={() => removeEmailAddress('ccAddresses', index)} />
                                            </Tag>
                                        ))}
                                    </Box>
                                    <FormErrorMessage>{errors.ccAddresses}</FormErrorMessage>
                                </FormControl>
                                
                                <FormControl isInvalid={!!errors.emailSubject}>
                                    <FormLabel>Email Subject</FormLabel>
                                    <Input
                                        value={formData.emailSubject}
                                        onChange={(e) => handleInputChange('emailSubject', e.target.value)}
                                        placeholder="Email subject line"
                                    />
                                    <FormErrorMessage>{errors.emailSubject}</FormErrorMessage>
                                </FormControl>
                                
                                <FormControl isInvalid={!!errors.emailBody}>
                                    <FormLabel>Email Body</FormLabel>
                                    <Textarea
                                        value={formData.emailBody}
                                        onChange={(e) => handleInputChange('emailBody', e.target.value)}
                                        placeholder="Enter email message body"
                                        rows={6}
                                    />
                                    <FormErrorMessage>{errors.emailBody}</FormErrorMessage>
                                </FormControl>
                            </VStack>
                        </Box>
                    </VStack>
                </ModalBody>
                
                <ModalFooter>
                    <Button variant="ghost" mr={3} onClick={onClose}>
                        Cancel
                    </Button>
                    <Button 
                        colorScheme="blue" 
                        onClick={handleSubmit}
                        isLoading={submitting}
                        loadingText="Saving..."
                    >
                        {initialData?.id ? 'Update Event' : 'Schedule Event'}
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

export default EventScheduleModal;